import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { Supplier, SupplierSearchData } from '../../models/supplier.model';
import { RootState, revertAll } from '../../store';
import { SupplierNote } from '../../models/supplier.note.model';
import { SupplierContact } from '../../models/supplier.contact.model';

export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (
    { company_id, page, sort, order, name, page_size }: SupplierSearchData,
    { rejectWithValue }
  ) => {
    if (company_id === undefined) {
      return rejectWithValue('Company ID is required');
    }

    const response = await NWClient.list('supplier_profile', {
      company_id,
      page,
      sort,
      order,
      name,
      page_size,
    });

    return response;
  }
);

export const fetchSupplierById = createAsyncThunk(
  'suppliers/fetchSupplierById',
  async (supplierId: number, { rejectWithValue }) => {
    try {
      const response = await NWClient.get('supplier_profile', supplierId);
      return response as Supplier;
    } catch (error) {
      rejectWithValue(error.message);
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async (updatedSupplier: Supplier, { rejectWithValue }) => {
    try {
      const response = await NWClient.put(
        'supplier_profile',
        updatedSupplier.id,
        updatedSupplier,
        true,
        false
      );
      return response as Supplier;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSupplierNotes = createAsyncThunk(
  'suppliers/fetchSupplierNotes',
  async (supplierId: number, { rejectWithValue }) => {
    if (supplierId === undefined) {
      return rejectWithValue('Supplier ID is required');
    }
    try {
      const response = (await NWClient.get('supplier_profile', supplierId)) as Supplier;
      return { supplierId, notes: response.notes as SupplierNote[] };
    } catch (error) {
      return rejectWithValue('Failed to fetch supplier notes');
    }
  }
);

export const updateSupplierNotes = createAsyncThunk(
  'suppliers/updateSupplierNotes',
  async ({ supplierId, note }: { supplierId: number; note: SupplierNote }, { dispatch }) => {
    try {
      await NWClient.put(`supplier-notes`, note.id, note);
      // After successful update, fetch updated notes to refresh state
      await dispatch(fetchSupplierNotes(supplierId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteSupplierNote = createAsyncThunk(
  'suppliers/deleteSupplierNote',
  async ({ supplierId, noteId }: { supplierId: number; noteId: number }, { dispatch }) => {
    try {
      await NWClient.delete(`supplier-notes`, noteId);
      // After successful deletion, fetch updated notes to refresh state
      await dispatch(fetchSupplierNotes(supplierId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const createSupplierContact = createAsyncThunk(
  'suppliers/createSupplierContact',
  async (
    {
      supplierId,
      contact,
    }: { supplierId: number; contact: Partial<SupplierContact>; company_id: number },
    { dispatch }
  ) => {
    try {
      const formData = new FormData();

      Object.entries(contact).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      const response = await NWClient.post('supplier_profile_contacts', formData, true);
      await dispatch(fetchSupplierById(supplierId));
      //await dispatch(fetchSuppliers({ company_id }));
      return { success: true, contact: response };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const updateSupplierContacts = createAsyncThunk(
  'suppliers/updateSupplierContacts',
  async (
    { supplierId, contact }: { supplierId: number; contact: SupplierContact; companyId: number },
    { dispatch }
  ) => {
    try {
      const formData = new FormData();

      Object.entries(contact).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      await NWClient.put('supplier_profile_contacts', contact.id, formData, true, true);
      await dispatch(fetchSupplierById(supplierId));
      //await dispatch(fetchSuppliers({ company_id: companyId }));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteSupplierContacts = createAsyncThunk(
  'suppliers/deleteSupplierContacts',
  async (
    { contactId, supplierId }: { companyId: number; contactId: number; supplierId?: number },
    { dispatch }
  ) => {
    try {
      await NWClient.delete('supplier_profile_contacts', contactId);
      await dispatch(fetchSupplierById(supplierId));
      //await dispatch(fetchSuppliers({ company_id: companyId }));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export interface SuppliersState {
  suppliersList: Supplier[];
  status: string;
  error: string;
  suppliersCount: number;
  notes: SupplierNote[];
  contacts: SupplierContact[];
  selectedSupplier: string;
  currentSupplierData: Supplier | null;
}

const suppliersArray: Supplier[] = [];
const initialState: SuppliersState = {
  suppliersList: suppliersArray,
  status: 'idle',
  error: '',
  suppliersCount: 0,
  notes: [],
  contacts: [],
  selectedSupplier: '',
  currentSupplierData: null,
};

export const setSelectedSupplier = createAction<string>('suppliers/setSelectedSupplier');

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearSupplierNotes(state) {
      state.notes = []; // Reset notes to an empty array
    },
    clearSupplierData(state) {
      state.currentSupplierData = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSuppliers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        if ('results' in action.payload && Array.isArray(action.payload.results)) {
          state.suppliersList = [...action.payload.results] as Supplier[];
          state.suppliersCount = action.payload.count || 0;
          state.status = 'succeeded';
        }
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateSupplier.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        const index = state.suppliersList.findIndex(
          (supplier) => supplier.id === action.payload.id
        );
        if (index !== -1) {
          state.suppliersList[index] = action.payload as Supplier;
        }
        state.currentSupplierData = action.payload as Supplier;
        state.status = 'succeeded';
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchSupplierById.pending, (state) => {
        state.status = 'supplierLoading';
      })
      .addCase(fetchSupplierById.fulfilled, (state, action) => {
        state.currentSupplierData = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchSupplierById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      .addCase(fetchSupplierNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSupplierNotes.fulfilled, (state, action) => {
        const { notes } = action.payload;
        state.notes = notes;
        state.status = 'succeeded';
      })
      .addCase(fetchSupplierNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch supplier notes';
      })
      .addCase(updateSupplierNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplierNotes.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateSupplierNotes.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to update supplier note';
      })
      .addCase(deleteSupplierNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteSupplierNote.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteSupplierNote.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to delete supplier note';
      })
      .addCase(createSupplierContact.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSupplierContact.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createSupplierContact.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to create supplier contact';
      })
      .addCase(updateSupplierContacts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplierContacts.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateSupplierContacts.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to update supplier contact';
      })
      .addCase(deleteSupplierContacts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteSupplierContacts.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteSupplierContacts.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to delete supplier contact';
      })
      .addCase(setSelectedSupplier, (state, action) => {
        state.selectedSupplier = action.payload;
      })
      .addCase(revertAll, () => initialState);
  },
});

export const { clearSupplierNotes, clearSupplierData } = suppliersSlice.actions;

export default suppliersSlice.reducer;

export const selectAllSuppliers = (state: RootState) => state.suppliers.suppliersList;
export const selectSupplierById = (state: RootState, supplierId: number) =>
  state.suppliers.suppliersList.find((item) => item.id === supplierId);
export const selectSupplierNotes = (state: RootState) => state.suppliers.notes;
export const selectSelectedSupplier = (state: RootState) => state.suppliers.selectedSupplier;
